import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import toast, { Toaster } from 'react-hot-toast';
import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/system';
import axios from '../../../../axios';
import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
import { DialogActions, Grid, IconButton, InputAdornment, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import { MuiColorInput } from 'mui-color-input';
import createNFTUpload from '../../../../assets/createNFTUpload.png';
import { ImageUpload } from '../../../components/DropZone/ImageUpload';
import { baseImageUrl } from '../../../utils/config';
import { UPDATE_SERVICE } from '../../../api';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DriveFileRenameOutlineRoundedIcon from '@mui/icons-material/DriveFileRenameOutlineRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';

function UpdateServiceModal({ open, handleClose, serviceData, retrieveService }) {
  const [loading, setLoading] = useState(false);
  const schema = Yup.object().shape({
    name: Yup.string().required('Service name is required'),
    slug: Yup.string().required('Service url is required'),
    title: Yup.string().required('Service title is required'),
    colour: Yup.string().required('Colour is required'),
  });
  const formik = useFormik({
    initialValues: {
      name: '',
      slug: '',
      title: '',
      description: '',
      status: '',
      type: 'Residential',
      colour: '#fff000',
      id: '',
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      setLoading(true);
      const formData = new FormData();
      formData.append('colour', values?.colour);
      formData.append('id', values?.id);
      formData.append('description', values?.description);
      formData.append('name', values?.name);
      formData.append('slug', values?.slug);
      formData.append('status', values?.status);
      formData.append('title', values?.title);
      formData.append('type', values?.type);
      if (values.image_1 instanceof File) {
        formData.append('image_1', values.image_1);
      }
      if (values.image_2 instanceof File) {
        formData.append('image_2', values.image_2);
      }
      toast.promise(
        axios.put(`${UPDATE_SERVICE}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        }),
        {
          loading: () => {
            return `Updating Service`;
          },
          success: (res) => {
            setLoading(false);
            setTimeout(() => {
              resetForm();
              handleClose();
              retrieveService();
            }, 1000);

            return res?.data?.message;
          },
          error: (err) => {
            setLoading(false);
            return err?.message;
          },
        }
      );
    },
  });

  const { errors, touched, resetForm, setFieldValue, handleSubmit, getFieldProps, values } = formik;
  useEffect(() => {
    setFieldValue('name', serviceData?.name);
    setFieldValue('slug', serviceData?.slug);
    setFieldValue('description', serviceData?.description);
    setFieldValue('title', serviceData?.title);
    setFieldValue('colour', serviceData?.colour);
    setFieldValue('status', serviceData?.status);
    setFieldValue('id', serviceData?.id);
    setFieldValue(
      'type',
      ['Home', 'Regular', 'Other', null, undefined, ''].includes(serviceData?.type)
        ? 'Residential'
        : serviceData?.type
    );
    setFieldValue(
      'image_1',
      serviceData?.image_1 ? `${baseImageUrl}${serviceData.image_1}` : null
    );
    setFieldValue(
      'image_2',
      serviceData?.image_2 ? `${baseImageUrl}${serviceData.image_2}` : null
    );
  }, [serviceData, setFieldValue]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { height: 'calc(100vh - 40px)', maxHeight: 820, display: 'flex', flexDirection: 'column', borderRadius: 3.5, overflow: 'hidden', boxShadow: '0 26px 80px rgba(15,35,70,.24)' } }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title" sx={{ px: { xs: 2, sm: 3 }, py: 2, display: 'flex', alignItems: 'center', gap: 1.4, bgcolor: '#f8fbff', borderBottom: '1px solid #e5edf6' }}>
        <Box sx={{ width: 42, height: 42, display: 'grid', placeItems: 'center', borderRadius: 2.5, bgcolor: '#e7f2ff', color: '#1976d2' }}><DriveFileRenameOutlineRoundedIcon /></Box>
        <Box sx={{ flex: 1 }}><Typography sx={{ color: '#10224a', fontSize: 19, fontWeight: 850 }}>Update Service Basics</Typography><Typography sx={{ mt: .2, color: '#718096', fontSize: 11 }}>Edit the customer-facing service information and media.</Typography></Box>
        <IconButton aria-label="Close" onClick={handleClose} size="small" sx={{ color: '#64748b' }}><CloseRoundedIcon /></IconButton>
      </DialogTitle>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit} style={{ display: 'flex', flex: 1, minHeight: 0, flexDirection: 'column', overflow: 'hidden' }}>
          <DialogContent sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: { xs: 2, sm: 3 }, py: '24px !important', bgcolor: '#fff' }}>
            <Grid container spacing={2}>
              <Grid item sm={6} xs={12}>
                <Tooltip title="A name will only appear internally to reference this booking page">
                  <TextField
                    size="small"
                    fullWidth
                    type="text"
                    label="Service Name*"
                    {...getFieldProps('name')}
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Tooltip>
              </Grid>
              <Grid item sm={6} xs={12}>
                <Tooltip
                  title="A consumer facing service title will appear in bookings and emails to identify
                  your service."
                >
                  <TextField
                    size="small"
                    fullWidth
                    type="text"
                    label="Service Title*"
                    {...getFieldProps('title')}
                    error={Boolean(touched.title && errors.title)}
                    helperText={touched.title && errors.title}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Tooltip>
              </Grid>

              <Grid item sm={12} xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  type="text"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ color: '#718096', fontSize: 12 }}>.../booking/</Typography>
                      </InputAdornment>
                    ),
                  }}
                  label="Service URL / Slug*"
                  {...getFieldProps('slug')}
                  error={Boolean(touched.slug && errors.slug)}
                  helperText={touched.slug && errors.slug}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <MuiColorInput
                  format="hex"
                  size="small"
                  label="Brand color"
                  value={values.colour}
                  error={Boolean(touched.colour && errors.colour)}
                  helperText={touched.colour && errors.colour}
                  onChange={(color) => setFieldValue('colour', color)}
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                  <TextField
                    size="small"
                    fullWidth
                    type="text"
                    select
                    label={'Type'}
                    {...getFieldProps('type')}
                    error={Boolean(touched.type && errors.type)}
                    helperText={touched.type && errors.type}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  >
                    <MenuItem value={'Residential'}>Residential</MenuItem>
                    <MenuItem value={'Commercial'}>Commercial</MenuItem>
                    <MenuItem value={'Custom'}>Custom</MenuItem>
                  </TextField>
                </Grid>
                <Grid item sm={12} xs={12}>
                  <TextField
                    size="small"
                    fullWidth
                    type="text"
                    label="Description"
                  multiline
                  minRows={3}
                    {...getFieldProps('description')}
                    error={Boolean(touched.description && errors.description)}
                    helperText={touched.description && errors.description}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ mt: .5, display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 34, height: 34, display: 'grid', placeItems: 'center', borderRadius: 2, bgcolor: '#edf5ff', color: '#1976d2' }}><ImageRoundedIcon fontSize="small" /></Box><Box><Typography sx={{ color: '#10224a', fontSize: 14, fontWeight: 850 }}>Service Media</Typography><Typography sx={{ color: '#718096', fontSize: 10.5 }}>Images shown on the customer booking page. Maximum size: 5 MB.</Typography></Box></Box>
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <Box sx={{ height: '100%', p: 1.5, border: '1px solid #dce6f1', borderRadius: 2.5, bgcolor: '#fbfdff' }}>
                <Typography sx={{ color: '#183153', fontSize: 12, fontWeight: 800 }}>Primary Image</Typography>
                <Typography sx={{ mb: 1, color: '#718096', fontSize: 10 }}>JPG, PNG, GIF, SVG, WEBM, TIFF, RAW or EPS</Typography>
                <ImageUpload
                  name="image_1"
                  Isvideo={false}
                  inputProps={{ accept: 'image/*' }}
                  src={values?.image_1 || createNFTUpload}
                  buttonProps={{
                    className: '!flex !justify-start !p-0 !py-4',
                  }}
                  onChange={(e, file) => {
                    setFieldValue(e, file);
                  }}
                  error={touched.profile_picture && Boolean(errors.profile_picture)}
                  helperText={touched.profile_picture && errors.profile_picture}
                />
                </Box>
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <Box sx={{ height: '100%', p: 1.5, border: '1px solid #dce6f1', borderRadius: 2.5, bgcolor: '#fbfdff' }}>
                <Typography sx={{ color: '#183153', fontSize: 12, fontWeight: 800 }}>Secondary Image</Typography>
                <Typography sx={{ mb: 1, color: '#718096', fontSize: 10 }}>JPG, PNG, GIF, SVG, WEBM, TIFF, RAW or EPS</Typography>
                <ImageUpload
                  name="image_2"
                  Isvideo={false}
                  inputProps={{ accept: 'image/*' }}
                  src={values?.image_2 || createNFTUpload}
                  buttonProps={{
                    className: '!flex !justify-start !p-0 !py-4',
                  }}
                  onChange={(e, file) => {
                    setFieldValue(e, file);
                  }}
                  error={touched.profile_picture && Boolean(errors.profile_picture)}
                  helperText={touched.profile_picture && errors.profile_picture}
                />
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ flexShrink: 0, px: { xs: 2, sm: 3 }, py: 1.8, borderTop: '1px solid #e5edf6', bgcolor: '#fbfdff' }}>
            <Button onClick={handleClose} variant="outlined" color="inherit" sx={{ minWidth: 94, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>Cancel</Button>
            <LoadingButton type="submit" color="primary" loading={loading} variant="contained" sx={{ minWidth: 126, borderRadius: 2, textTransform: 'none', fontWeight: 750 }}>Save Changes</LoadingButton>
          </DialogActions>
        </Form>
      </FormikProvider>
      <Toaster position="top-right" />
    </Dialog>
  );
}

export default UpdateServiceModal;
